import { FormEvent, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChartNoAxesColumn, CheckCircle, CircleUser, Donut, SlidersHorizontal, X } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { deleteConversations, saveUserPreferences } from "@/api/api";
import ErrorDetails from "@/components/Message/ErrorDetails";
import { getErrorDetails, getErrorSummary } from "@/utils/errorMessages";
import { DEFAULT_PERSONALIZATION, Personalization, PersonalizationTone } from "@/api/models";
import { doTrackEvent, toggleTheme } from "@/utils/utils";
import { cn } from "@/lib/utils";
import UsageBar from "@/components/Sidebar/UsageBar";
import ProfileAnalytics from "@/components/Sidebar/ProfileAnalytics";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type SettingsTab = 'general' | 'profile' | 'personalization' | 'usage';

const toneOptions: { value: PersonalizationTone; label: string; description: string }[] = [
  { value: 'default', label: 'Default', description: 'Balanced and adaptable' },
  { value: 'professional', label: 'Professional', description: 'Polished and precise' },
  { value: 'friendly', label: 'Friendly', description: 'Warm and conversational' },
  { value: 'candid', label: 'Candid', description: 'Direct and encouraging' },
  { value: 'quirky', label: 'Quirky', description: 'Playful and imaginative' },
  { value: 'efficient', label: 'Efficient', description: 'Concise and plain' },
];

const promptOverridePattern = /\b(?:jailbreak|prompt injection|ignore\s+(?:all\s+|any\s+)?(?:(?:previous|above|prior)\s+)?instructions?|(?:disregard|override|bypass)\s+(?:the\s+)?(?:system|developer|previous|instructions?)|you\s+are\s+(?:now\s+)?(?:the\s+)?system|(?:reveal|show|print|output|repeat|extract|expose)\s+(?:the\s+)?(?:system\s+prompt|system\s+instructions?))\b/i;
const personalizationInstructionsError = 'Custom instructions rejected - jailbreak attempt detected';

/**
 * One control language for the whole dialog: identical height, radius, border,
 * hover and focus ring across every button, input, textarea and select. The
 * global `outline: unset !important` reset means focus has to be a ring, never
 * an outline.
 */
const focusRing = "focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-surface";
const buttonBase = `inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg px-3.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${focusRing}`;
const primaryButton = `${buttonBase} bg-primary text-white shadow-[0_1px_2px_rgba(0,0,0,0.14)] hover:bg-primary/90`;
const neutralButton = `${buttonBase} border border-line-strong bg-surface text-ink hover:bg-hover`;
const dangerButton = `${buttonBase} border border-error/40 text-error hover:border-error/60 hover:bg-error/10 focus-visible:ring-error/50 dark:border-red-400/40 dark:text-red-400 dark:hover:border-red-400/60 dark:hover:bg-red-400/10`;

/** Recessed field surface: canvas sits below `surface` in both themes. */
const fieldBase = "w-full rounded-lg border border-line bg-canvas text-[13px] text-ink shadow-none transition-colors placeholder:text-ink-3 hover:border-line-strong focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";
const inputClass = `${fieldBase} h-9 px-3`;
const textareaClass = `${fieldBase} min-h-[7.5rem] resize-y px-3 py-2.5 leading-relaxed`;
const selectTriggerClass = `${fieldBase} h-9 justify-between gap-2 px-3 data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/25`;
const selectContentClass = "rounded-xl border-line bg-surface p-1 shadow-lg";
const selectItemClass = "cursor-pointer rounded-lg py-2 pl-3 pr-9 text-[13px] text-ink focus:bg-hover focus:text-ink";

/** Page header for a tab body. Type scale and space carry the hierarchy -- no rule. */
const Panel = ({ title, description, children }: { title: string; description?: string; children: ReactNode }) => (
  <>
    <header className="mb-7">
      <h2 className="text-[1.375rem] font-semibold leading-tight tracking-[-0.015em] text-ink">{title}</h2>
      {description && <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">{description}</p>}
    </header>
    {children}
  </>
);

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-2">{children}</h3>
);

/** Grouping container -- one hairline around a set of rows instead of a rule under each. */
const SettingsCard = ({ className, children }: { className?: string; children: ReactNode }) => (
  <div className={cn("divide-y divide-line overflow-hidden rounded-xl border border-line", className)}>{children}</div>
);

const SettingsRow = ({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor?: string; children: ReactNode }) => (
  <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
    <div className="min-w-0">
      {htmlFor
        ? <label htmlFor={htmlFor} className="block text-sm font-medium text-ink">{label}</label>
        : <div className="text-sm font-medium text-ink">{label}</div>}
      {hint && <p className="mt-1 text-xs leading-relaxed text-ink-2">{hint}</p>}
    </div>
    <div className="w-full shrink-0 sm:w-auto">{children}</div>
  </div>
);

interface SettingsProps {
  isSettingsDialogOpen: boolean;
  setIsSettingsDialogOpen: (open: boolean) => void;
  activeTab: SettingsTab;
  setActiveTab: (tab: SettingsTab) => void;
}

const Settings = ({ isSettingsDialogOpen, setIsSettingsDialogOpen, activeTab, setActiveTab }: SettingsProps) => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [selectPortalContainer, setSelectPortalContainer] = useState<HTMLDivElement | null>(null);
  const setContentRef = useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    setSelectPortalContainer(node);
  }, []);
  const FF_JOYRIDE = appStateContext?.state.frontendSettings?.feature_flags?.joyride || 0;
  const VERSION = appStateContext?.state.frontendSettings?.version_number || '';
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const handleThemeChange = () => toggleTheme(appStateContext);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const personalization = appStateContext?.state.personalization || DEFAULT_PERSONALIZATION;
  const personalizationLoading = appStateContext?.state.personalizationLoading ?? true;
  const personalizationError = appStateContext?.state.personalizationError;
  const [personalizationDraft, setPersonalizationDraft] = useState<Personalization>(personalization);
  const [isSavingPersonalization, setIsSavingPersonalization] = useState(false);
  const [personalizationSaveError, setPersonalizationSaveError] = useState<unknown>(null);
  const [personalizationSaveErrorDetails, setPersonalizationSaveErrorDetails] = useState<ReturnType<typeof getErrorDetails> | null>(null);
  const [personalizationSaved, setPersonalizationSaved] = useState(false);

  useEffect(() => {
    if (!personalizationLoading) setPersonalizationDraft(personalization);
  }, [personalization, personalizationLoading]);

  const updatePersonalization = <K extends keyof Personalization>(field: K, value: Personalization[K]) => {
    setPersonalizationDraft((current) => ({ ...current, [field]: value }));
    setPersonalizationSaved(false);
    setPersonalizationSaveError(null);
    setPersonalizationSaveErrorDetails(null);
  };

  const hasUnsafeCustomInstructions = promptOverridePattern.test(personalizationDraft.custom_instructions);

  const handleSavePersonalization = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (hasUnsafeCustomInstructions) {
      setPersonalizationSaveError(personalizationInstructionsError);
      setPersonalizationSaveErrorDetails(null);
      return;
    }
    setIsSavingPersonalization(true);
    setPersonalizationSaved(false);
    setPersonalizationSaveError(null);
    setPersonalizationSaveErrorDetails(null);
    try {
      const savedPreferences = await saveUserPreferences(personalizationDraft);
      appStateContext?.dispatch({ type: 'LOAD_PERSONALIZATION', payload: savedPreferences });
      setPersonalizationSaved(true);
      setTimeout(() => setPersonalizationSaved(false), 3000);
    } catch (error) {
      const details = getErrorDetails(error);
      setPersonalizationSaveError(error);
      setPersonalizationSaveErrorDetails(details);
    } finally {
      setIsSavingPersonalization(false);
    }
  };

  const tabs: { id: SettingsTab; label: string; icon: typeof SlidersHorizontal }[] = [
    { id: 'general', label: 'General', icon: SlidersHorizontal },
    { id: 'profile' as SettingsTab, label: 'Profile', icon: CircleUser },
    { id: 'personalization' as SettingsTab, label: 'Personalization', icon: Donut },
    { id: 'usage' as SettingsTab, label: 'Usage', icon: ChartNoAxesColumn },
  ];

  /**
   * Handle facilitating the deletion of all conversations
  **/
  const handleDeleteAllChats = async () => {
    // Confirm user wants to delete all conversations
    if (confirm("Are you sure you want to delete all of your conversations?")) {
      setIsDeleting(true);
      await deleteConversations();
      navigate('/');
      handleTrackEvent('click', 'delete_all_chats', 'Delete All Chats', '', '', '');
      window.location.reload();
    }
  }

  const handleSelectTab = (tab: SettingsTab) => {
    setActiveTab(tab);
    handleTrackEvent('click', `settings_tab_${tab}`, `Settings - ${tab} Tab`, '', '', '');
  }

  return (
    <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
      <DialogContent
        ref={setContentRef}
        tabIndex={-1}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.focus({ preventScroll: true });
        }}
        className="h-[min(52rem,calc(100dvh-2rem))] w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] max-w-4xl overflow-hidden rounded-3xl border-transparent bg-surface p-0 gap-0 shadow-elevated [&>button]:hidden"
        aria-describedby={undefined}
      >
        <div className="flex h-full min-h-0 min-w-0 w-full max-w-full flex-col sm:flex-row">
          {/* Navigation rail -- a real surface step below the content pane in both themes */}
          <div className="flex shrink-0 flex-col border-b border-line bg-canvas p-3 sm:w-56 sm:border-b-0 sm:border-e sm:p-4">
            <div className="mb-3 flex items-center gap-2 sm:mb-5">
              <DialogClose
                className={`inline-flex size-8 shrink-0 items-center justify-center rounded-xl text-ink-2 transition-colors hover:bg-hover hover:text-ink focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas`}
              >
                <X className="size-4" aria-hidden="true" />
                <span className="sr-only">Close settings</span>
              </DialogClose>
              <DialogTitle className="text-[15px] font-semibold tracking-tight text-ink">Settings</DialogTitle>
              <span className="ms-auto text-[11px] tabular-nums text-ink-3 sm:hidden">v{VERSION}</span>
            </div>

            <nav
              className="-mx-1 flex min-w-0 max-w-full touch-pan-x flex-nowrap gap-1 overflow-x-auto overscroll-x-contain px-1 pb-1 [scrollbar-width:none] [-webkit-overflow-scrolling:touch] sm:mx-0 sm:flex-col sm:overflow-visible sm:px-0 sm:pb-0 [&::-webkit-scrollbar]:hidden"
              aria-label="Settings sections"
            >
              {tabs.map(({ id, label, icon: Icon }) => {
                const isActive = activeTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      "group flex h-9 shrink-0 items-center gap-2.5 rounded-lg px-2.5 text-[13px] transition-colors sm:w-full mt-1 sm:mt-0",
                      focusRing,
                      "focus-visible:ring-offset-canvas",
                      isActive
                        ? "bg-surface font-semibold text-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)] ring-1 ring-line"
                        : "font-medium text-ink-2 hover:bg-hover hover:text-ink",
                    )}
                    onClick={() => handleSelectTab(id)}
                  >
                    <Icon
                      className={cn("size-[17px] shrink-0 transition-colors", isActive ? "text-primary" : "text-ink-3 group-hover:text-ink-2")}
                      aria-hidden="true"
                    />
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="mt-auto hidden px-2.5 pt-6 text-[11px] tabular-nums text-ink-3 sm:block">v{VERSION}</div>
          </div>

          {/* Tab content */}
          <div className="min-h-0 min-w-0 w-full max-w-full flex-1 overflow-x-hidden overflow-y-auto bg-surface p-6 scrollbar scrollbar-gutter-stable scrollbar-thumb-line-strong scrollbar-track-transparent scrollbar-w-8 sm:p-8">
            {activeTab === 'general' &&
              <div>
                <Panel title="General" description="Appearance and account-wide actions.">
                  <SectionLabel>Preferences</SectionLabel>
                  <SettingsCard>
                    <SettingsRow label="Theme" hint="Match the interface to your surroundings.">
                      <Select value={appStateContext?.state.theme} onValueChange={() => { handleThemeChange(); handleTrackEvent('click', 'theme_change_settings', 'Theme Changed (Settings)', '', '', ''); }}>
                        <SelectTrigger className={`${selectTriggerClass} sm:w-36`} aria-label="Theme">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent container={selectPortalContainer ?? undefined} className={selectContentClass}>
                          <SelectItem className={selectItemClass} value="dark">Dark</SelectItem>
                          <SelectItem className={selectItemClass} value="light">Light</SelectItem>
                        </SelectContent>
                      </Select>
                    </SettingsRow>

                    {FF_JOYRIDE == 1 && !window.matchMedia('(max-width: 700px)').matches &&
                      <SettingsRow label="Restart tour" hint="Replay the guided walkthrough from the beginning.">
                        <button type="button" className={`${neutralButton} w-full sm:w-auto`} onClick={() => { localStorage.removeItem('sms-gpt_joyrideComplete'); handleTrackEvent('click', 'restart_joyride', 'Restart Joyride', '', '', ''); window.location.reload(); }}>Restart</button>
                      </SettingsRow>
                    }
                  </SettingsCard>

                  <div className="mt-8">
                    <SectionLabel>Data</SectionLabel>
                    <SettingsCard>
                      <SettingsRow label="Delete all chats" hint="Permanently removes every conversation from your history. This cannot be undone.">
                        <button type="button" className={`${dangerButton} w-full min-w-[7rem] sm:w-auto`} onClick={handleDeleteAllChats} disabled={isDeleting}>
                          {!isDeleting ? 'Delete All' : <span className="loading loading-spinner loading-xs" aria-hidden="true"></span>}
                        </button>
                      </SettingsRow>
                    </SettingsCard>
                  </div>
                </Panel>
              </div>
            }

            {activeTab === 'profile' &&
              <div>
                <Panel title="Profile" description="A review of your chat activity.">
                  <ProfileAnalytics />
                </Panel>
              </div>
            }

            {activeTab === 'personalization' &&
              <form onSubmit={handleSavePersonalization}>
                <Panel title="Personalization" description="Customize how responses are written for you.">
                  {personalizationLoading ? (
                    <div className="flex min-h-56 items-center justify-center">
                      <span className="loading loading-ring text-primary"></span>
                    </div>
                  ) : (
                    <>
                      <SectionLabel>Response style</SectionLabel>
                      <SettingsCard>
                        <SettingsRow
                          label="Base style and tone"
                          hint="This guides response style without changing model capabilities."
                          htmlFor="personalization-tone"
                        >
                          <Select value={personalizationDraft.tone} onValueChange={(value) => updatePersonalization('tone', value as PersonalizationTone)}>
                            <SelectTrigger id="personalization-tone" className={`${selectTriggerClass} sm:w-44`}>
                              <SelectValue>{toneOptions.find((option) => option.value === personalizationDraft.tone)?.label}</SelectValue>
                            </SelectTrigger>
                            <SelectContent container={selectPortalContainer ?? undefined} className={`${selectContentClass} min-w-[15rem]`} align="end">
                              {toneOptions.map((option) => (
                                <SelectItem key={option.value} className={selectItemClass} value={option.value}>
                                  <div className="flex flex-col items-start gap-0.5">
                                    <span className="font-medium text-ink">{option.label}</span>
                                    <span className="text-[11px] text-ink-2">{option.description}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </SettingsRow>

                        <div className="px-4 py-4">
                          <div className="flex items-baseline justify-between gap-3">
                            <label htmlFor="personalization-instructions" className="text-sm font-medium text-ink">Custom instructions</label>
                            <span className="text-[11px] tabular-nums text-ink-3">{personalizationDraft.custom_instructions.length}/2000</span>
                          </div>
                          <p className="mt-1 text-xs leading-relaxed text-ink-2">Response style and context preferences only. System or model instructions cannot be changed here.</p>
                          <textarea
                            id="personalization-instructions"
                            value={personalizationDraft.custom_instructions}
                            onChange={(event) => updatePersonalization('custom_instructions', event.target.value)}
                            maxLength={2000}
                            rows={5}
                            aria-invalid={hasUnsafeCustomInstructions}
                            aria-describedby={hasUnsafeCustomInstructions ? 'personalization-instructions-error' : undefined}
                            className={`${textareaClass} mt-3 ${hasUnsafeCustomInstructions ? 'border-error focus:border-error focus:ring-error/25' : ''}`}
                            placeholder="Additional behavior, style, and tone preferences"
                          />
                          {hasUnsafeCustomInstructions &&
                            <p id="personalization-instructions-error" className="mt-2 text-xs text-error dark:text-red-400" role="alert">{personalizationInstructionsError}</p>
                          }
                        </div>
                      </SettingsCard>

                      <div className="mt-8">
                        <SectionLabel>About you</SectionLabel>
                        <SettingsCard>
                          <SettingsRow label="Nickname" htmlFor="personalization-nickname">
                            <input
                              id="personalization-nickname"
                              type="text"
                              value={personalizationDraft.nickname}
                              onChange={(event) => updatePersonalization('nickname', event.target.value)}
                              maxLength={100}
                              className={`${inputClass} sm:w-64`}
                              placeholder="What should we call you?"
                            />
                          </SettingsRow>
                          <SettingsRow label="Occupation" htmlFor="personalization-occupation">
                            <input
                              id="personalization-occupation"
                              type="text"
                              value={personalizationDraft.occupation}
                              onChange={(event) => updatePersonalization('occupation', event.target.value)}
                              maxLength={160}
                              className={`${inputClass} sm:w-64`}
                              placeholder="What do you do?"
                            />
                          </SettingsRow>
                        </SettingsCard>
                      </div>

                      {(personalizationError || personalizationSaveError) && (() => {
                        const error = personalizationSaveError || personalizationError;
                        const details = personalizationSaveError ? personalizationSaveErrorDetails : personalizationError instanceof Error ? getErrorDetails(personalizationError) : null;
                        return (
                          <div className="mt-6 text-xs text-error dark:text-red-400" role="alert">
                            <p>{details ? getErrorSummary(error, details) : String(error)}</p>
                            {details && <ErrorDetails details={details} error={error} />}
                          </div>
                        );
                      })()}

                      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-line pt-5">
                        <button type="submit" className={`${primaryButton} min-w-[7.5rem]`} disabled={isSavingPersonalization || hasUnsafeCustomInstructions}>
                          {isSavingPersonalization ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : 'Save changes'}
                        </button>
                        {personalizationSaved &&
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success dark:text-emerald-400" role="status" aria-live="polite">
                            <CheckCircle className="size-4" aria-hidden="true" />Saved
                          </span>
                        }
                      </div>
                    </>
                  )}
                </Panel>
              </form>
            }

            {activeTab === 'usage' &&
              <div>
                <Panel title="Usage Limits" description="Track weekly token usage.">
                  <UsageBar />
                </Panel>
              </div>
            }
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default Settings;
